import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { CellWrap } from "@/components/board/cells/CellWrap";
import { Popover } from "@/components/ui/Popover";
import { useDateFormat } from "@/hooks/useDateFormat";
import { relDate, fmtMonthYear } from "@/lib/format";
import { t } from "@/lib/strings";
import { BRAND } from "@/lib/tokens";

interface DateCellProps {
  value: string | null;
  rowH?: number;
  onClick?: () => void;
  today?: string;
  /** When the row is completed (resolved ticket / checked task) the due date is no
   * longer actionable — it renders as a plain date, never "überfällig"/"heute". */
  done?: boolean;
  /** Resolution timestamp — if it's a later day than the due date, the plain date
   * gets a subtle "verspätet" marker (done-but-late). */
  resolvedAt?: string | null;
  onPatch?: (value: string | null) => void;
}

const PAD = (n: number) => n.toString().padStart(2, "0");

function toIso(d: Date): string {
  return `${d.getFullYear()}-${PAD(d.getMonth() + 1)}-${PAD(d.getDate())}`;
}

function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.slice(0, 10));
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
}

function formatDdMmYyyy(d: Date): string {
  return `${PAD(d.getDate())}.${PAD(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function parseUserInput(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  let y: number, mo: number, d: number;
  const dot = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s);
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (dot) {
    d = Number(dot[1]);
    mo = Number(dot[2]) - 1;
    y = Number(dot[3]);
  } else if (iso) {
    y = Number(iso[1]);
    mo = Number(iso[2]) - 1;
    d = Number(iso[3]);
  } else {
    return null;
  }
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return toIso(dt);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function buildGrid(viewMonth: Date): Date[] {
  const first = startOfMonth(viewMonth);
  const offset = (first.getDay() + 6) % 7;
  const start = addDays(first, -offset);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) cells.push(addDays(start, i));
  return cells;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

const DOW = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function DisplayInner({
  value,
  today,
  done,
  resolvedAt,
}: {
  value: string | null;
  today: string;
  done?: boolean;
  resolvedAt?: string | null;
}) {
  const [dateFormat] = useDateFormat();
  if (!value) {
    return (
      <span aria-label="Date cell" style={{ fontSize: 12.5, color: "var(--ink-4)" }}>
        —
      </span>
    );
  }
  const rel = relDate(value, today, done, resolvedAt, dateFormat);
  if (!rel) {
    return (
      <span aria-label="Date cell" style={{ fontSize: 12.5, color: "var(--ink-4)" }}>
        —
      </span>
    );
  }
  // var(--ink-1) is #0d3e63 in light (identical to BRAND.blueDark) and #e4ecf2
  // in dark — the hardcoded navy was invisible on the dark surface.
  let color: string = "var(--ink-1)";
  let bg = "transparent";
  if (rel.overdue) {
    color = "var(--danger)";
    bg = "rgba(184, 82, 73, 0.10)";
  } else if (rel.today) {
    color = "var(--warn)";
    bg = "rgba(183, 128, 41, 0.12)";
  }
  return (
    <span
      aria-label="Date cell"
      title={value}
      style={{
        fontSize: 12.5,
        color,
        padding: rel.overdue || rel.today ? "3px 8px" : "0",
        borderRadius: 6,
        background: bg,
        fontWeight: rel.overdue ? 500 : 400,
        fontFeatureSettings: '"tnum"',
      }}
    >
      {rel.label}
      {rel.late ? (
        <span style={{ marginLeft: 6, fontSize: 11, color: "var(--warn)", fontWeight: 500 }}>
          {t.resolvedLate}
        </span>
      ) : null}
    </span>
  );
}

function ReadOnlyDisplay({
  value,
  rowH,
  onClick,
  today,
  done,
  resolvedAt,
}: {
  value: string | null;
  rowH: number;
  onClick?: () => void;
  today: string;
  done?: boolean;
  resolvedAt?: string | null;
}) {
  return (
    <CellWrap rowH={rowH} {...(onClick ? { onClick } : {})}>
      <DisplayInner value={value} today={today} done={done} resolvedAt={resolvedAt} />
    </CellWrap>
  );
}

export function DateCell({
  value,
  rowH = 44,
  onClick,
  today,
  done,
  resolvedAt,
  onPatch,
}: DateCellProps) {
  const ref = today ?? new Date().toISOString().slice(0, 10);
  // Read-only cells must not set up the editor's hooks (a table can render
  // hundreds) — split so hooks are never conditional.
  if (!onPatch) {
    return (
      <ReadOnlyDisplay
        value={value}
        rowH={rowH}
        onClick={onClick}
        today={ref}
        done={done}
        resolvedAt={resolvedAt}
      />
    );
  }
  return (
    <EditableDateCell
      value={value}
      rowH={rowH}
      today={ref}
      done={done}
      resolvedAt={resolvedAt}
      onPatch={onPatch}
    />
  );
}

function EditableDateCell({
  value,
  rowH,
  today,
  done,
  resolvedAt,
  onPatch,
}: {
  value: string | null;
  rowH: number;
  today: string;
  done?: boolean;
  resolvedAt?: string | null;
  onPatch: (value: string | null) => void;
}) {
  const cellRef = useRef<HTMLDivElement | null>(null);
  const nativeInputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, []);

  const handleCellClick = () => {
    if (isMobile) {
      const el = nativeInputRef.current;
      if (el) {
        el.focus();
        const anyEl = el as HTMLInputElement & { showPicker?: () => void };
        if (typeof anyEl.showPicker === "function") {
          try {
            anyEl.showPicker();
          } catch {
            /* no-op */
          }
        }
      }
    } else {
      setOpen(true);
    }
  };

  return (
    <>
      <CellWrap rowH={rowH} onClick={handleCellClick}>
        <div ref={cellRef} style={{ width: "100%", display: "flex", alignItems: "center" }}>
          <DisplayInner value={value} today={today} done={done} resolvedAt={resolvedAt} />
        </div>
        {isMobile ? (
          <input
            ref={nativeInputRef}
            type="date"
            value={value ?? ""}
            onChange={(e) => {
              const v = e.currentTarget.value;
              onPatch(v ? v : null);
            }}
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0,
              pointerEvents: "none",
              border: "none",
              background: "transparent",
            }}
            aria-hidden="true"
            tabIndex={-1}
          />
        ) : null}
      </CellWrap>
      {open && !isMobile ? (
        <CalendarPopover
          anchor={cellRef.current}
          value={value}
          onClose={() => setOpen(false)}
          onCommit={(iso) => {
            onPatch(iso);
            setOpen(false);
          }}
          onClear={() => {
            onPatch(null);
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

interface CalendarPopoverProps {
  anchor: HTMLElement | null;
  value: string | null;
  onClose: () => void;
  onCommit: (iso: string) => void;
  onClear: () => void;
}

function CalendarPopover({ anchor, value, onClose, onCommit, onClear }: CalendarPopoverProps) {
  const selected = useMemo(() => parseIso(value), [value]);
  const todayDate = useMemo(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  const [viewMonth, setViewMonth] = useState<Date>(() => startOfMonth(selected ?? todayDate));
  const [highlight, setHighlight] = useState<Date>(selected ?? todayDate);
  const [text, setText] = useState<string>(selected ? formatDdMmYyyy(selected) : "");
  const [inputInvalid, setInputInvalid] = useState(false);

  const gridRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!sameMonth(highlight, viewMonth)) {
      setViewMonth(startOfMonth(highlight));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight]);

  const cells = useMemo(() => buildGrid(viewMonth), [viewMonth]);

  const focusGrid = () => {
    requestAnimationFrame(() => gridRef.current?.focus());
  };

  const onGridKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      onCommit(toIso(highlight));
      return;
    }
    let next: Date | null = null;
    if (e.key === "ArrowLeft") next = addDays(highlight, -1);
    else if (e.key === "ArrowRight") next = addDays(highlight, 1);
    else if (e.key === "ArrowUp") next = addDays(highlight, -7);
    else if (e.key === "ArrowDown") next = addDays(highlight, 7);
    else if (e.key === "PageUp") next = addMonths(highlight, -1);
    else if (e.key === "PageDown") next = addMonths(highlight, 1);
    if (next) {
      e.preventDefault();
      e.stopPropagation();
      setHighlight(next);
    }
  };

  const onInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const iso = parseUserInput(text);
      if (iso) {
        setInputInvalid(false);
        onCommit(iso);
      } else {
        setInputInvalid(true);
      }
      return;
    }
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      focusGrid();
    }
  };

  const cellBaseStyle = (d: Date): React.CSSProperties => {
    const inMonth = sameMonth(d, viewMonth);
    const isSelected = selected && sameDay(d, selected);
    const isHighlight = sameDay(d, highlight);
    const isToday = sameDay(d, todayDate);
    const base: React.CSSProperties = {
      width: 32,
      height: 28,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 6,
      fontSize: 12.5,
      fontFeatureSettings: '"tnum"',
      cursor: "pointer",
      color: inMonth ? "var(--ink-1)" : "var(--ink-4)",
      background: "transparent",
      border: "1px solid transparent",
      boxSizing: "border-box",
      userSelect: "none",
    };
    if (isToday && !isSelected) {
      base.border = "1px solid var(--ink-3)";
    }
    if (isSelected) {
      base.background = BRAND.blue;
      base.color = "#fff";
      base.border = `1px solid ${BRAND.blue}`;
    }
    if (isHighlight && !isSelected) {
      base.background = "var(--surface-soft)";
    }
    return base;
  };

  return (
    <Popover anchor={anchor} onClose={onClose} width={260}>
      <div
        style={{ padding: 10, fontFamily: "Inter Variable, Inter, system-ui, sans-serif" }}
        onKeyDown={(e) => {
          if (
            [
              "ArrowUp",
              "ArrowDown",
              "ArrowLeft",
              "ArrowRight",
              "Enter",
              "Escape",
              "PageUp",
              "PageDown",
            ].includes(e.key)
          ) {
            e.stopPropagation();
          }
        }}
      >
        <input
          ref={inputRef}
          autoFocus
          value={text}
          onChange={(e) => {
            setText(e.currentTarget.value);
            if (inputInvalid) setInputInvalid(false);
          }}
          onKeyDown={onInputKey}
          placeholder="TT.MM.JJJJ"
          aria-label="Datum eingeben"
          style={{
            width: "100%",
            height: 32,
            boxSizing: "border-box",
            padding: "0 10px",
            fontSize: 13,
            fontFamily: "inherit",
            color: "var(--ink-1)",
            background: "var(--surface)",
            border: `1px solid ${inputInvalid ? "var(--danger)" : "var(--hairline-strong)"}`,
            borderRadius: 6,
            outline: "none",
            fontFeatureSettings: '"tnum"',
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 10,
            marginBottom: 6,
          }}
        >
          <button
            type="button"
            aria-label="Vorheriger Monat"
            onClick={() => {
              const next = addMonths(viewMonth, -1);
              setViewMonth(next);
              setHighlight(
                new Date(next.getFullYear(), next.getMonth(), Math.min(highlight.getDate(), 28)),
              );
            }}
            style={chevronStyle}
          >
            ◀
          </button>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--ink-1)",
              textTransform: "capitalize",
            }}
          >
            {fmtMonthYear(viewMonth)}
          </div>
          <button
            type="button"
            aria-label="Nächster Monat"
            onClick={() => {
              const next = addMonths(viewMonth, 1);
              setViewMonth(next);
              setHighlight(
                new Date(next.getFullYear(), next.getMonth(), Math.min(highlight.getDate(), 28)),
              );
            }}
            style={chevronStyle}
          >
            ▶
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 2,
            marginBottom: 2,
          }}
        >
          {DOW.map((d) => (
            <div
              key={d}
              style={{
                textAlign: "center",
                fontSize: 10.5,
                fontWeight: 600,
                color: "var(--ink-3)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                padding: "4px 0",
              }}
            >
              {d}
            </div>
          ))}
        </div>

        <div
          ref={gridRef}
          tabIndex={0}
          role="grid"
          aria-label="Kalender"
          onKeyDown={onGridKey}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 2,
            outline: "none",
          }}
        >
          {cells.map((d) => {
            const iso = toIso(d);
            return (
              <button
                key={iso}
                type="button"
                role="gridcell"
                aria-selected={selected ? sameDay(d, selected) : false}
                onClick={(e) => {
                  e.stopPropagation();
                  onCommit(iso);
                }}
                // Only highlight on hover for days IN the shown month — hovering the
                // dimmed leading/trailing days (top/bottom rows) used to flip the view
                // forward/back via the highlight→viewMonth effect, racing through months.
                onMouseEnter={() => {
                  if (sameMonth(d, viewMonth)) setHighlight(d);
                }}
                style={cellBaseStyle(d)}
              >
                {d.getDate()}
              </button>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 10,
            paddingTop: 8,
            borderTop: "1px solid var(--hairline)",
          }}
        >
          <button
            type="button"
            onClick={() => {
              setViewMonth(startOfMonth(todayDate));
              setHighlight(todayDate);
              setText(formatDdMmYyyy(todayDate));
              onCommit(toIso(todayDate));
            }}
            style={footerBtnStyle}
          >
            Heute
          </button>
          {value ? (
            <button
              type="button"
              onClick={() => onClear()}
              style={{ ...footerBtnStyle, color: "var(--danger)" }}
            >
              Löschen
            </button>
          ) : (
            <span />
          )}
        </div>
      </div>
    </Popover>
  );
}

const chevronStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  border: "1px solid transparent",
  background: "transparent",
  borderRadius: 6,
  cursor: "pointer",
  color: "var(--ink-2)",
  fontSize: 11,
  lineHeight: 1,
};

const footerBtnStyle: React.CSSProperties = {
  border: "1px solid var(--hairline)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  borderRadius: 6,
  padding: "5px 10px",
  fontSize: 12,
  fontFamily: "inherit",
  cursor: "pointer",
};
